import '../css/app.css';
import './bootstrap';
import './lib/vh';
import './lib/noScroll';
import { closeStuckModals, startOverlayWatcher } from './lib/overlayGuard';
import { registerLeadPostServiceWorker } from './lib/serviceWorker';
import { startOnlineHeartbeat, stopOnlineHeartbeat } from '@/lib/onlineClient';
import { installStaffClickLinkGuard, setStaffClickConfig } from '@/lib/staffClickLinks';

import { createInertiaApp, router } from '@inertiajs/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { ConfirmProvider } from '@/Components/ConfirmDialog';
import LocalDevAccountSwitcher from '@/Components/LocalDevAccountSwitcher';
import { ThemeProvider } from '@/Components/layout/ThemeProvider';
import { getAuthAccessOrDefaults } from '@/lib/roles';

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';
const assetReloadQuery = '__asset_reload';

registerLeadPostServiceWorker();

const pages = import.meta.glob('./Pages/**/*.{jsx,tsx}');

function chatRuntimePathFrom(rawUrl?: unknown): string {
    if (typeof window === 'undefined') return '';

    if (rawUrl instanceof URL) {
        return rawUrl.pathname || '';
    }

    if (typeof rawUrl === 'string' && rawUrl.trim() !== '') {
        try {
            return new URL(rawUrl, window.location.origin).pathname || '';
        } catch {}
    }

    return window.location.pathname || '';
}

function preloadChatRuntime(rawUrl?: unknown): void {
    const path = chatRuntimePathFrom(rawUrl);
    if (!path.startsWith('/chats')) return;

    void import('@/Components/chat/ChatWindow');
    void import('@/Components/chat/Composer');
}

preloadChatRuntime();

const historyResumeRefreshCooldownMs = 1500;

function shouldRefreshAfterHistoryResume(pathname: string): boolean {
    const path = pathname.replace(/\/+$/, '') || '/';

    return (
        path === '/dashboard'
        || path === '/chats'
        || path.startsWith('/chats/')
        || path === '/admin'
        || path.startsWith('/admin/')
        || path === '/clients'
        || path.startsWith('/clients/')
        || path === '/partners'
        || path.startsWith('/partners/')
        || path === '/tracking'
        || path.startsWith('/tracking/')
        || path === '/finance'
        || path.startsWith('/finance/')
    );
}

function installHistoryResumeRefreshGuard(): void {
    if (typeof window === 'undefined') return;

    let lastRefreshAt = 0;
    let pendingPopstateAt = 0;

    const requestRefresh = (reason: string) => {
        try {
            window.dispatchEvent(new CustomEvent('lp:history-resume', { detail: { reason } }));
        } catch {}

        const pathname = window.location.pathname || '/';
        if (!shouldRefreshAfterHistoryResume(pathname)) return;

        const now = Date.now();
        if (now - lastRefreshAt < historyResumeRefreshCooldownMs) return;
        lastRefreshAt = now;

        window.setTimeout(() => {
            try {
                router.reload({
                    preserveScroll: true,
                    preserveState: false,
                });
            } catch {}
        }, 80);
    };

    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
        let restoredFromHistory = event.persisted === true;

        try {
            const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
            restoredFromHistory = restoredFromHistory || navigation?.type === 'back_forward';
        } catch {}

        if (restoredFromHistory) {
            requestRefresh('pageshow');
        }
    });

    window.addEventListener('popstate', () => {
        pendingPopstateAt = Date.now();

        window.setTimeout(() => {
            if (pendingPopstateAt > 0 && Date.now() - pendingPopstateAt >= 100) {
                pendingPopstateAt = 0;
                requestRefresh('popstate');
            }
        }, 120);
    });

    try {
        router.on('navigate', () => {
            if (pendingPopstateAt <= 0 || Date.now() - pendingPopstateAt > 1000) return;
            pendingPopstateAt = 0;
            requestRefresh('inertia-history');
        });
    } catch {}
}

type RuntimeErrorBoundaryProps = {
    children: ReactNode;
};

type RuntimeErrorBoundaryState = {
    hasError: boolean;
};

function cleanAssetReloadQuery(): void {
    try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has(assetReloadQuery)) return;
        url.searchParams.delete(assetReloadQuery);
        window.history.replaceState({}, '', url.toString());
    } catch {}
}

async function clearRuntimeCaches(): Promise<void> {
    try {
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map((registration) => registration.unregister()));
        }
    } catch {}

    try {
        if ('caches' in window) {
            const cacheNames = await window.caches.keys();
            await Promise.all(
                cacheNames
                    .filter((cacheName) => cacheName.startsWith('leadpost-static-'))
                    .map((cacheName) => window.caches.delete(cacheName)),
            );
        }
    } catch {}
}

async function reloadWithFreshAssets(nextUrl?: URL): Promise<void> {
    await clearRuntimeCaches();

    try {
        const url = nextUrl ?? new URL(window.location.href);
        url.searchParams.set(assetReloadQuery, String(Date.now()));
        window.location.replace(url.toString());
    } catch {
        try { window.location.reload(); } catch {}
    }
}

function renderStartupFallback(error?: unknown): void {
    const el = document.getElementById('app');
    if (!el) return;

    try {
        console.error('leadpost_inertia_boot_failed', error);
    } catch {}

    el.innerHTML = [
        '<div class="flex min-h-screen items-center justify-center bg-background px-4 text-foreground">',
        '<div class="w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-lg">',
        '<div class="text-base font-semibold">Интерфейс не загрузился</div>',
        '<div class="mt-2 text-sm text-muted-foreground">Страница открылась со старой или повреждённой сборкой. Сбросьте кэш страницы и откройте свежую версию.</div>',
        '<div class="mt-4 flex flex-wrap gap-2">',
        '<button type="button" data-lp-reload class="inline-flex h-9 items-center rounded-md border border-border bg-background px-3 text-sm font-medium hover:bg-accent">Обновить</button>',
        '<button type="button" data-lp-reset class="inline-flex h-9 items-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:opacity-90">Сбросить кэш</button>',
        '</div>',
        '</div>',
        '</div>',
    ].join('');

    el.querySelector('[data-lp-reload]')?.addEventListener('click', () => {
        window.location.reload();
    });
    el.querySelector('[data-lp-reset]')?.addEventListener('click', () => {
        void reloadWithFreshAssets();
    });
}

class RuntimeErrorBoundary extends Component<RuntimeErrorBoundaryProps, RuntimeErrorBoundaryState> {
    state: RuntimeErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError(): RuntimeErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        try {
            console.error('leadpost_runtime_render_failed', error, info);
        } catch {}
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        return (
            <div className="flex min-h-screen items-center justify-center bg-background px-4 text-foreground">
                <div className="w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-lg">
                    <div className="text-base font-semibold">Интерфейс не загрузился</div>
                    <div className="mt-2 text-sm text-muted-foreground">
                        Обновите страницу. Если экран повторится, сбросьте кэш этой страницы.
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <button
                            type="button"
                            className="inline-flex h-9 items-center rounded-md border border-border bg-background px-3 text-sm font-medium hover:bg-accent"
                            onClick={() => window.location.reload()}
                        >
                            Обновить
                        </button>
                        <button
                            type="button"
                            className="inline-flex h-9 items-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:opacity-90"
                            onClick={() => void reloadWithFreshAssets()}
                        >
                            Сбросить кэш
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

void createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: async (name) => {
        const page = pages[`./Pages/${name}.tsx`] ?? pages[`./Pages/${name}.jsx`];

        if (!page) {
            throw new Error(`Page not found: ${name}`);
        }

        const module = await page();

        return module.default;
    },
    setup({ el, App, props }) {
        cleanAssetReloadQuery();
        const root = createRoot(el);

        root.render(
            <ThemeProvider>
                <ConfirmProvider>
                    <RuntimeErrorBoundary>
                        <App {...props} />
                        <LocalDevAccountSwitcher initialPageProps={props.initialPage.props as any} />
                    </RuntimeErrorBoundary>
                </ConfirmProvider>
            </ThemeProvider>
        );

        // Safety net: close any stuck modals on Inertia navigations
        try {
            const sweep = () => { closeStuckModals(); setTimeout(closeStuckModals, 60); setTimeout(closeStuckModals, 200); };
            router.on('start', (event: any) => {
                preloadChatRuntime(event?.detail?.visit?.url);
                sweep();
            });
            router.on('finish', sweep);
            router.on('error', sweep);
        } catch {}

        // Continuous watcher: hides large blank iframes/overlays injected by extensions or UI libs
        try { startOverlayWatcher(); } catch {}

        // Browser Back/Forward can restore stale Inertia props while realtime state has moved on.
        try { installHistoryResumeRefreshGuard(); } catch {}

        // CRM-opened managed links go through an internal redirect that marks the click as staff/admin.
        try {
            const syncStaffClickLinks = (page: any) => {
                setStaffClickConfig(page?.props?.auth?.staff_click || null);
            };

            installStaffClickLinkGuard((props.initialPage.props as any)?.auth?.staff_click || null);
            router.on('navigate', (e: any) => syncStaffClickLinks(e?.detail?.page));
        } catch {}

        // Online heartbeat is published by admins and network curators; shared online view stays admin-only.
        try {
            const sync = (page: any) => {
                const access = getAuthAccessOrDefaults(page?.props);
                if (access.canManageSystem || access.isNetworkCurator) {
                    startOnlineHeartbeat();
                } else {
                    stopOnlineHeartbeat();
                }
            };

            sync(props.initialPage);
            router.on('navigate', (e: any) => sync(e?.detail?.page));
        } catch {}
    },
    progress: {
        color: '#4B5563',
    },
}).catch((error) => {
    renderStartupFallback(error);
});
